import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../i18n';

const Dashboard = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [stats, setStats] = useState({
    unread: 0,
    total: 0,
    sent: 0
  });
  const [recentEmails, setRecentEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cryptoAddresses, setCryptoAddresses] = useState({});
  const [showCrypto, setShowCrypto] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    fetchCryptoAddresses();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [unreadRes, inboxRes, sentRes] = await Promise.all([
        axios.get('/api/emails/stats/unread'),
        axios.get('/api/emails/inbox?limit=5'),
        axios.get('/api/emails/sent?limit=5')
      ]);

      setStats({
        unread: unreadRes.data.unread,
        total: inboxRes.data.pagination?.total || inboxRes.data.emails?.length || 0,
        sent: sentRes.data.pagination?.total || sentRes.data.emails?.length || 0
      });

      setRecentEmails(inboxRes.data.emails || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCryptoAddresses = async () => {
    try {
      const response = await axios.get('/api/users/crypto-addresses');
      setCryptoAddresses(response.data);
    } catch (error) {
      console.error('Failed to fetch crypto addresses:', error);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Address copied to clipboard!');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '50px' }}>{t('common.loading')}</div>;
  }

  return (
    <div>
      <div className="dashboard-header">
        <h1>{t('dashboard.welcome')}, {user?.username}!</h1>
        <p>{t('dashboard.title')}</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <h3>{t('dashboard.stats.emails')}</h3>
          <div className="value">{stats.unread}</div>
        </div>
        <div className="stat-card">
          <h3>{t('inbox.title')}</h3>
          <div className="value">{stats.total}</div>
        </div>
        <div className="stat-card">
          <h3>{t('dashboard.stats.sent')}</h3>
          <div className="value">{stats.sent}</div>
        </div>
        <div className="stat-card">
          <h3>Subscription</h3>
          <div className="value" style={{ fontSize: '20px' }}>
            {user?.subscription_tier?.toUpperCase() || 'FREE'}
          </div>
        </div>
      </div>

      {/* Crypto Addresses Section */}
      <div className="card" style={{ marginTop: '30px', borderColor: '#f59e0b' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h2 style={{ color: '#f59e0b' }}>💰 {t('landing.features.crypto.title')}</h2>
          <button 
            className="btn btn-secondary"
            onClick={() => setShowCrypto(!showCrypto)}
          >
            {showCrypto ? t('common.close') : t('common.search')} Addresses
          </button>
        </div>
        
        <p style={{ color: '#9ca3af', marginBottom: '15px' }}>
          Use these addresses to upgrade your subscription with cryptocurrency:
        </p>

        {showCrypto && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            {cryptoAddresses.btc_address && (
              <div style={{ backgroundColor: '#0a0a0a', padding: '15px', borderRadius: '6px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ color: '#f59e0b', fontWeight: 'bold' }}>Bitcoin (BTC)</span>
                  <button 
                    className="btn btn-secondary" 
                    style={{ padding: '5px 10px', fontSize: '12px' }}
                    onClick={() => copyToClipboard(cryptoAddresses.btc_address)}
                  >
                    📋 Copy
                  </button>
                </div>
                <code style={{ fontSize: '12px', wordBreak: 'break-all' }}>
                  {cryptoAddresses.btc_address}
                </code>
              </div>
            )}

            {cryptoAddresses.eth_address && (
              <div style={{ backgroundColor: '#0a0a0a', padding: '15px', borderRadius: '6px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ color: '#627eea', fontWeight: 'bold' }}>Ethereum (ETH)</span>
                  <button 
                    className="btn btn-secondary" 
                    style={{ padding: '5px 10px', fontSize: '12px' }}
                    onClick={() => copyToClipboard(cryptoAddresses.eth_address)}
                  >
                    📋 Copy
                  </button>
                </div>
                <code style={{ fontSize: '12px', wordBreak: 'break-all' }}>
                  {cryptoAddresses.eth_address}
                </code>
              </div>
            )}

            {cryptoAddresses.sol_address && (
              <div style={{ backgroundColor: '#0a0a0a', padding: '15px', borderRadius: '6px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ color: '#14f195', fontWeight: 'bold' }}>Solana (SOL)</span>
                  <button 
                    className="btn btn-secondary" 
                    style={{ padding: '5px 10px', fontSize: '12px' }}
                    onClick={() => copyToClipboard(cryptoAddresses.sol_address)}
                  >
                    📋 Copy
                  </button>
                </div>
                <code style={{ fontSize: '12px', wordBreak: 'break-all' }}>
                  {cryptoAddresses.sol_address}
                </code>
              </div>
            )}
          </div>
        )}

        <div style={{ marginTop: '15px' }}>
          <Link to="/upgrade" className="btn btn-primary">
            💎 Upgrade with Crypto →
          </Link>
        </div>
      </div>

      <div className="card" style={{ marginTop: '30px' }}>
        <h2 style={{ marginBottom: '20px' }}>{t('dashboard.recentEmails')}</h2>
        
        {recentEmails.length === 0 ? (
          <p style={{ color: '#9ca3af', textAlign: 'center', padding: '30px' }}>
            {t('dashboard.noEmails')}
          </p>
        ) : (
          <div className="email-list">
            {recentEmails.map((email) => (
              <Link
                key={email.id}
                to={`/inbox?email=${email.id}`}
                className={`email-item ${!email.is_read ? 'unread' : ''}`}
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <div className="email-sender">{email.sender}</div>
                <div className="email-subject">
                  {email.subject || '(No subject)'}
                </div>
                <div className="email-date">{formatDate(email.received_at)}</div>
              </Link>
            ))}
          </div>
        )}

        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          <Link to="/inbox" className="btn btn-secondary">
            {t('dashboard.viewAll')}
          </Link>
        </div>
      </div>

      <div className="card" style={{ marginTop: '30px' }}>
        <h2 style={{ marginBottom: '15px' }}>{t('settings.email')}</h2>
        <p style={{ color: '#9ca3af', marginBottom: '15px' }}>
          Share this address to receive anonymous emails:
        </p>
        <div style={{ 
          backgroundColor: '#0a0a0a', 
          padding: '15px', 
          borderRadius: '6px',
          fontFamily: 'monospace',
          wordBreak: 'break-all'
        }}>
          {user?.username}@tormal.onion
        </div>
        <button 
          className="btn btn-secondary" 
          style={{ marginTop: '10px' }}
          onClick={() => copyToClipboard(`${user?.username}@tormal.onion`)}
        >
          📋 {t('tempmail.copy')}
        </button>
      </div>

      {/* Quick Actions */}
      <div className="card" style={{ marginTop: '30px' }}>
        <h2 style={{ marginBottom: '20px' }}>{t('dashboard.quickActions.title')}</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px' }}>
          <Link to="/compose" className="btn btn-primary" style={{ textAlign: 'center' }}>
            ✉️ {t('nav.compose')}
          </Link>
          <Link to="/tempmail" className="btn btn-secondary" style={{ textAlign: 'center' }}>
            🕐 {t('nav.tempmail')}
          </Link>
          <Link to="/pgp" className="btn btn-secondary" style={{ textAlign: 'center' }}>
            🔐 {t('nav.pgp')}
          </Link>
          <Link to="/settings" className="btn btn-secondary" style={{ textAlign: 'center' }}>
            ⚙️ {t('nav.settings')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;



